import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, ModalFooter, Checkbox, Row, Col } from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import createDecorator from 'final-form-focus';

import FormFieldsGenerator from '../../../../../../../shared/FormFieldsGenerator';

const focusOnError = createDecorator();

function EditHoldingModal({
  open,
  onClose,
  onSave,
  holdingData,
  template,
  controlledVocab,
  itemsFields,
  onUpdateItems,
}) {
  const [updateItemsLocations, setUpdateItemsLocations] = useState(false);
  // Handle form submission with validation
  const handleSubmit = (values) => {
    // Check for required fields validation
    const errors = {};

    // Get holding template fields
    const holdingFields = template?.templateFields?.holdings || [];

    // Validate required fields
    holdingFields.forEach((section) => {
      if (section.sectionFields) {
        section.sectionFields.forEach((field) => {
          if (
            field.isRequired &&
            (!values[field.name] || values[field.name] === '')
          ) {
            errors[field.name] = (
              <FormattedMessage
                id="ui-plugin-inventory-templates.validation.required"
                defaultMessage="This field is required"
              />
            );
          }
        });
      }
    });

    // If validation errors exist, return them to prevent submission
    if (Object.keys(errors).length > 0) {
      return errors;
    }

    // If validation passes, save the holding
    onSave(values, updateItemsLocations);
    return undefined;
  };

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={holdingData}
      mutators={{ ...arrayMutators }}
      decorators={[focusOnError]}
      render={({ handleSubmit: formSubmit, submitting }) => {
        // Create footer JSX without memoization to avoid hooks rule violation
        const renderFooter = (
          <ModalFooter>
            <Button
              onClick={formSubmit}
              buttonStyle="primary"
              marginBottom0
              disabled={submitting}
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.holdingManagement.editModal.save"
                defaultMessage="Save Changes"
              />
            </Button>
            <Button onClick={onClose} buttonStyle="secondary" marginBottom0>
              <FormattedMessage
                id="ui-plugin-inventory-templates.holdingManagement.editModal.cancel"
                defaultMessage="Cancel"
              />
            </Button>
          </ModalFooter>
        );

        return (
          <Modal
            open={open}
            onClose={onClose}
            footer={renderFooter}
            label={
              <FormattedMessage
                id="ui-plugin-inventory-templates.holdingManagement.editModal.title"
                defaultMessage="Edit Holding"
              />
            }
            size="large"
            wrappingElement="form"
          >
            <form onSubmit={formSubmit}>
              <div style={{ padding: '20px' }}>
                <p style={{ marginBottom: '20px', color: '#6b7280' }}>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.holdingManagement.editModal.description"
                    values={{
                      holdingNumber: holdingData?.holdingCopyNumber || '?',
                    }}
                    defaultMessage="Edit the holding {holdingNumber} details below. Required fields are marked with an asterisk."
                  />
                </p>

                {/* Option to update items locations */}
                {itemsFields?.value?.length > 0 && (
                  <Row>
                    <Col xs={12}>
                      <div style={{ 
                        padding: '12px', 
                        backgroundColor: '#f8f9fa', 
                        border: '1px solid #dee2e6', 
                        borderRadius: '4px', 
                        marginBottom: '20px' 
                      }}>
                        <Checkbox
                          checked={updateItemsLocations}
                          onChange={(e) => setUpdateItemsLocations(e.target.checked)}
                          label={
                            <FormattedMessage
                              id="ui-plugin-inventory-templates.holdingManagement.editModal.updateItemsLocations"
                              defaultMessage="Update items locations to match holding locations"
                            />
                          }
                          onMouseDown={(e) => e.preventDefault()}
                        />
                        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                          <FormattedMessage
                            id="ui-plugin-inventory-templates.holdingManagement.editModal.updateItemsLocations.description"
                            values={{ count: itemsFields.value.filter(item => item.holdingsRecordId === holdingData.id).length }}
                            defaultMessage="This will update the permanent and temporary locations of {count} items belonging to this holding"
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}

                {/* Generate form fields from template */}
                <FormFieldsGenerator
                  key="edit-holding-template-fields"
                  formValues={template?.templateFields?.holdings || []}
                  pageName="" // Empty pageName so fields use direct field names
                  controlledVocab={controlledVocab}
                />
              </div>
            </form>
          </Modal>
        );
      }}
    />
  );
}

EditHoldingModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  holdingData: PropTypes.object,
  template: PropTypes.object,
  controlledVocab: PropTypes.object,
  itemsFields: PropTypes.object,
  onUpdateItems: PropTypes.func,
};

export default EditHoldingModal;
